/**
 * Utilitários para extração de códigos de domínio de objetos de propriedade
 */

const resolvePropertiesRoot = (propertyData) => {
  if (!propertyData || typeof propertyData !== 'object') return {};

  if (propertyData.gn_properties && typeof propertyData.gn_properties === 'object') {
    return propertyData.gn_properties;
  }

  if (propertyData.properties && typeof propertyData.properties === 'object') {
    return propertyData.properties;
  }

  if (propertyData.data && typeof propertyData.data === 'object') {
    if (propertyData.data.gn_properties && typeof propertyData.data.gn_properties === 'object') {
      return propertyData.data.gn_properties;
    }
    if (propertyData.data.properties && typeof propertyData.data.properties === 'object') {
      return propertyData.data.properties;
    }
  }

  return {};
};

/**
 * Função auxiliar para extrair códigos CAR
 * @param {Object} propertyData - Dados da propriedade
 * @returns {Array} Lista de códigos CAR
 */
export const extractCarCodes = (propertyData) => {
  const properties = resolvePropertiesRoot(propertyData);
  if (!Array.isArray(properties.car)) {
    return [];
  }

  return properties.car.map((item) => item.cod_imovel).filter(Boolean);
};

/**
 * Função auxiliar para extrair códigos SIGEF
 * @param {Object} propertyData - Dados da propriedade
 * @returns {Array} Lista de códigos SIGEF
 */
export const extractSigefCodes = (propertyData) => {
  const properties = resolvePropertiesRoot(propertyData);
  if (!Array.isArray(properties.sigef)) {
    return [];
  }

  return properties.sigef.map((item) => item.codigo_imo).filter(Boolean);
};

/**
 * Função para contar o número de parcelas SIGEF
 * @param {Object} propertyData - Dados da propriedade
 * @returns {number} Número de parcelas SIGEF
 */
export const countSigefParcels = (propertyData) => {
  const properties = resolvePropertiesRoot(propertyData);
  if (!Array.isArray(properties.sigef)) {
    return 0;
  }

  let totalParcels = 0;

  for (const sigefItem of properties.sigef) {
    if (sigefItem.parcels && Array.isArray(sigefItem.parcels)) {
      totalParcels += sigefItem.parcels.length;
    } else if (sigefItem.parcela_co && Array.isArray(sigefItem.parcela_co)) {
      totalParcels += sigefItem.parcela_co.length;
    }
  }

  return totalParcels;
};

/**
 * Função auxiliar para extrair códigos NIRF (CAFIR)
 * @param {Object} propertyData - Dados da propriedade
 * @returns {Array} Lista de códigos NIRF
 */
export const extractNirfCodes = (propertyData) => {
  try {
    const properties = resolvePropertiesRoot(propertyData);
    const cafirData = properties.CAFIR || properties.cafir;
    if (!cafirData) return [];

    const nirfFieldNames = ['nirf', 'NIRF', 'codigo_nirf', 'CODIGO_NIRF'];
    const accumulator = new Set();

    const collectFromObject = (obj) => {
      if (!obj || typeof obj !== 'object') return;
      for (const field of nirfFieldNames) {
        const value = obj?.[field];
        if (typeof value === 'string' && value.trim()) {
          accumulator.add(value.trim());
        }
      }
      if (Array.isArray(obj.details)) {
        obj.details.forEach(collectFromObject);
      }
    };

    if (Array.isArray(cafirData)) {
      cafirData.forEach(collectFromObject);
    } else {
      collectFromObject(cafirData);
    }

    return Array.from(accumulator);
  } catch (_error) {
    return [];
  }
};

/**
 * Função auxiliar para extrair código SNCR
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string|null} Código SNCR ou null
 */
export const extractSncrCode = (propertyData) => {
  try {
    const properties = resolvePropertiesRoot(propertyData);
    const sncrData = properties.SNCR || properties.sncr;
    if (!sncrData) return null;

    const pickCode = (record) => {
      if (!record || typeof record !== 'object') return null;
      return record.codigo_sncr || record.codigo_incra || record.codigo_imovel_incra || null;
    };

    if (Array.isArray(sncrData)) {
      for (const item of sncrData) {
        const code = pickCode(item);
        if (code) return code;
      }
      return null;
    }

    return pickCode(sncrData);
  } catch (_error) {
    //console.error('[domainCodeExtractor] Erro ao extrair código SNCR:', error);
    return null;
  }
};

/**
 * Extrai a chave INCRA relacionada ao SNCR (caso exista)
 * @param {Object} propertyData - Dados da propriedade
 * @returns {string|null} Código INCRA encontrado no bloco SNCR
 */
export const extractSncrIncraCode = (propertyData) => {
  const properties = resolvePropertiesRoot(propertyData);
  const sncrData = properties.SNCR || properties.sncr;
  if (!sncrData) return null;

  const extractFromRecord = (record) => {
    if (!record || typeof record !== 'object') return null;
    return (
      record.codigo_incra ||
      record.codigo_imovel_incra ||
      record.codigoIncra ||
      record.codigoImovelIncra ||
      null
    );
  };

  if (Array.isArray(sncrData)) {
    for (const item of sncrData) {
      const value = extractFromRecord(item);
      if (value) return value;
    }
    return null;
  }

  if (Array.isArray(sncrData.details)) {
    for (const detail of sncrData.details) {
      const value = extractFromRecord(detail);
      if (value) return value;
    }
  }

  return extractFromRecord(sncrData);
};
